import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Leaflet + React-Leaflet
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

const stepVariants = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.2, ease: "easeIn" } },
};

const blackBtnStyle = {
  backgroundImage: `url(${process.env.PUBLIC_URL}/Black2.png)`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  color: "#fff",
};

const whiteBtnStyle = {
  backgroundImage: `url(${process.env.PUBLIC_URL}/White2.png)`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  color: "#000",
};

const API_BASE = process.env.REACT_APP_API_BASE || "";

// helper to upload a File via presign endpoint and return the S3 key
const uploadToS3ViaPresign = async (file) => {
  if (!file) return null;
  const ext = (file.name?.split('.').pop() || 'bin').toLowerCase();
  const { data: presign } = await axios.post(`${API_BASE}/api/s3/presign-upload`, {
    contentType: file.type || 'application/octet-stream',
    ext
  });
  await fetch(presign.url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  });
  return presign.key;
};

/* -------------------------
   Small presentational components
   ------------------------- */
const InfoPanel = ({ title, children }) => (
  <div className="p-4 rounded-lg border-l-4 border-purple-600 bg-purple-50 text-sm text-gray-800 shadow-sm">
    <div className="font-semibold text-[#40BEB4] mb-1">{title}</div>
    <div className="text-xs leading-relaxed">{children}</div>
  </div>
);

const WarningBox = ({ children }) => (
  <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm shadow-inner">
    <div className="font-semibold mb-1">Warning</div>
    <div className="text-xs leading-relaxed">{children}</div>
  </div>
);

const QuestionCard = ({ children }) => (
  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 shadow-sm">{children}</div>
);

// Shared input/label styles for light theme
const inputCls = "border border-gray-300 p-2 w-full rounded bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#40BEB4]";
const labelCls = "block mb-1 font-semibold text-gray-700";

/* -------------------------
   Component
   ------------------------- */
const KycForm = () => {
  const [step, setStep] = useState(1);
  const [consent, setConsent] = useState(false);
  const [form, setForm] = useState({
    aadhaar: "",
    file: null,
    name: "",
    father: "",
    dob: "",
    mobile: "",
    address: "",
    state: "",
    pincode: "",
    candidatePhoto: null,
    latitude: "",
    longitude: "",
    aadhaarPhoto: null,
    serialNumbers: [],

    // updated additional info fields
    convicted: "",
    appliedBefore: "",
    licenseSuspended: "",
    familyHasLicence: "",
    safePlaceToKeep: "",
    trainingUnderRule10: "",
    purposeDetails: "",

    // new fields
    carryArea: "", // "throughout" | "state" | "district"
    trainingCertificate: null, // File
    extraDocument: null, // File
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(true);

  // DigiLocker
  const [digilockerStatus, setDigilockerStatus] = useState(null);
  const [digilockerVerificationId, setDigilockerVerificationId] = useState("");
  const [digilockerDoc, setDigilockerDoc] = useState(null);
  const digilockerVerified = digilockerStatus === "AUTHENTICATED" && !!digilockerDoc;

  const [aadhaarPhotoUrl, setAadhaarPhotoUrl] = useState("");
  const [aadhaarPhotoPreview, setAadhaarPhotoPreview] = useState("");

  const navigate = useNavigate();
  const cameraStreamRef = useRef(null);
  const videoRef = useRef(null);

  const DEFAULT_CENTER = [12.9716, 77.5946];
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  useEffect(() => {
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter([lat, lng]);
    }
  }, [form.latitude, form.longitude]);

  const FlyToCenter = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      map.flyTo(center, map.getZoom(), { animate: true });
    }, [center, map]);
    return null;
  };

  const ClickToSetLatLng = () => {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setForm((prev) => ({
          ...prev,
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6),
        }));
      },
    });
    return null;
  };

  const startCamera = async (front = useFrontCamera) => {
    try {
      const constraints = { video: { facingMode: front ? "user" : "environment" } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      cameraStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.warn("Camera selection failed, falling back:", err);
      try {
        const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        }
        cameraStreamRef.current = fallbackStream;
        if (videoRef.current) videoRef.current.srcObject = fallbackStream;
      } catch (finalErr) {
        console.error("No camera available:", finalErr);
        alert("No available camera detected on this device.");
      }
    }
  };

  const toggleCamera = () => {
    const newFront = !useFrontCamera;
    setUseFrontCamera(newFront);
    startCamera(newFront);
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
  };

  const getAge = (isoDate) => {
    if (!isoDate) return null;
    const [y, m, d] = isoDate.split("-").map(Number);
    const today = new Date();
    let age = today.getFullYear() - y;
    const mDiff = (today.getMonth() + 1) - m;
    if (mDiff < 0 || (mDiff === 0 && today.getDate() < d)) age--;
    return age;
  };

  const normalizeDob = (raw) => {
    if (!raw || typeof raw !== "string") return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
    return raw;
  };

  const pickMobile = (obj) => {
    const candidate =
      obj?.mobile ?? obj?.phone ?? obj?.phone_number ?? obj?.user_details?.mobile ?? obj?.user_details?.phone ?? "";
    const digits = String(candidate).replace(/\D/g, "");
    return digits.length >= 10 ? digits.slice(-10) : "";
  };

  const fetchRemoteImageAsFile = async (url, filename = "aadhaar-photo.jpg") => {
    const cacheBusted = url + (url.includes("?") ? "&" : "?") + "_cb=" + Date.now();
    const res = await axios.get(cacheBusted, {
      responseType: "blob",
      withCredentials: false,
      headers: { "Cache-Control": "no-cache" },
    });
    const blob = res.data;
    const type = blob?.type || "image/jpeg";
    return new File([blob], filename, { type });
  };

  const looksLikeBase64 = (s) => {
    if (!s || typeof s !== "string") return false;
    if (/^https?:\/\//i.test(s)) return false;
    const cleaned = s.replace(/\s+/g, "");
    return cleaned.length > 256 && /^[A-Za-z0-9+/=]+$/.test(cleaned.slice(0, 1024));
  };

  const dataUrlToFile = async (dataUrl, filename = "aadhaar-photo.jpg") => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const type = blob.type || "image/jpeg";
    return new File([blob], filename, { type });
  };

  const base64ToFile = async (base64, filename = "aadhaar-photo.jpg", mime = "image/jpeg") => {
    if (/^data:/i.test(base64)) return dataUrlToFile(base64, filename);
    const dataUrl = `data:${mime};base64,${base64}`;
    return dataUrlToFile(dataUrl, filename);
  };

  const startDigilocker = async () => {
    try {
      setErrorMsg("");
      const userId = `webuser_${Date.now()}`;

      const res = await axios.post(
        `${API_BASE}/api/digilocker/create-url`,
        { userId, documents: ["AADHAAR"], flow: "signin" },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data?.url) {
        if (res.data.verification_id) setDigilockerVerificationId(res.data.verification_id);
        window.location.href = res.data.url;
      } else {
        setErrorMsg("Failed to create DigiLocker link. Please try again.");
      }
    } catch (e) {
      console.error("Create DigiLocker URL error:", e?.response?.data || e);
      setErrorMsg(e?.response?.data?.details || "Error creating DigiLocker link.");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vId = params.get("verification_id");
    if (!vId) return;

    setDigilockerVerificationId(vId);
    setErrorMsg("");
    setDigilockerStatus("PENDING");

    let timer;

    const poll = async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/digilocker/status`, {
          params: { verification_id: vId },
        });

        const status = r.data?.status;
        setDigilockerStatus(status || null);

        const mobileFromStatus = pickMobile(r.data);
        if (mobileFromStatus) {
          setForm((prev) => (prev.mobile ? prev : { ...prev, mobile: mobileFromStatus }));
        }

        if (status === "AUTHENTICATED") {
          const d = await axios.get(`${API_BASE}/api/digilocker/document/AADHAAR`, {
            params: { verification_id: vId },
          });
          const doc = d.data || {};
          setDigilockerDoc(doc);

          try {
            const photoValue =
              doc?.photo_url ||
              doc?.photo ||
              doc?.image_url ||
              doc?.photo_link ||
              doc?.image ||
              doc?.portrait ||
              doc?.photo_base64 ||
              doc?.image_base64 ||
              "";

            if (photoValue) {
              setAadhaarPhotoUrl(photoValue);

              if (/^data:/i.test(photoValue)) {
                const file = await dataUrlToFile(photoValue, "aadhaar-photo.jpg");
                setForm((prev) => ({ ...prev, aadhaarPhoto: file }));
                setAadhaarPhotoPreview(URL.createObjectURL(file));
              } else if (looksLikeBase64(photoValue)) {
                const file = await base64ToFile(photoValue, "aadhaar-photo.jpg", "image/jpeg");
                setForm((prev) => ({ ...prev, aadhaarPhoto: file }));
                setAadhaarPhotoPreview(URL.createObjectURL(file));
              } else if (/^https?:\/\//i.test(photoValue)) {
                try {
                  const file = await fetchRemoteImageAsFile(photoValue, "aadhaar-photo.jpg");
                  setForm((prev) => ({ ...prev, aadhaarPhoto: file }));
                  setAadhaarPhotoPreview(URL.createObjectURL(file));
                } catch (err) {
                  console.warn("Could not fetch Aadhaar photo as file from URL, using direct preview:", err);
                  setAadhaarPhotoPreview(photoValue);
                }
              } else {
                try {
                  const file = await base64ToFile(photoValue, "aadhaar-photo.jpg", "image/jpeg");
                  setForm((prev) => ({ ...prev, aadhaarPhoto: file }));
                  setAadhaarPhotoPreview(URL.createObjectURL(file));
                } catch (e) {
                  console.warn("Unrecognized Aadhaar photo format");
                }
              }
            }
          } catch (photoErr) {
            console.warn("Aadhaar photo handling failed:", photoErr);
          }

          const fullName = doc?.name || doc?.full_name || "";
          const fatherName = doc?.father_name || doc?.care_of || "";

          const rawDob = doc?.dob || doc?.date_of_birth || "";
          const dob = normalizeDob(rawDob);

          const mobileFromDoc = pickMobile(doc);

          const splitAddr =
            (typeof doc?.split_address === "object" && doc?.split_address) ||
            (typeof doc?.address === "object" && doc?.address) ||
            doc?.split_address ||
            doc?.address ||
            {};

          const addressLine =
            typeof splitAddr === "string"
              ? splitAddr
              : [
                  splitAddr?.house,
                  splitAddr?.street,
                  splitAddr?.landmark,
                  splitAddr?.vtc,
                  splitAddr?.po,
                  splitAddr?.subdist,
                  splitAddr?.dist,
                ]
                  .filter(Boolean)
                  .join(", ");

          const state = splitAddr?.state || doc?.state || "";
          const pincode = splitAddr?.pincode || splitAddr?.pin || doc?.pincode || "";

          setForm((prev) => ({
            ...prev,
            name: prev.name || fullName,
            father: prev.father || fatherName,
            dob: prev.dob || dob,
            mobile: prev.mobile || mobileFromDoc,
            address: prev.address || addressLine,
            state: prev.state || state,
            pincode: prev.pincode || (pincode ? String(pincode) : ""),
            aadhaar: prev.aadhaar || doc?.uid_masked || "",
          }));

          window.history.replaceState({}, document.title, window.location.pathname);

          setErrorMsg("✅ DigiLocker verified. Aadhaar details fetched.");
          clearInterval(timer);
        } else if (status === "EXPIRED" || status === "CONSENT_DENIED") {
          clearInterval(timer);
          setErrorMsg(
            status === "EXPIRED"
              ? "DigiLocker link expired. Please start again."
              : "Consent denied. Please try again if this was accidental."
          );
        }
      } catch (e) {
        console.error("Status poll error:", e?.response?.data || e);
      }
    };

    poll();
    timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, []);

  const handleChange = (e) => {
    const { name, files, value } = e.target;

    let newValue = value;
    if (name === "pincode") {
      newValue = value.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "latitude" || name === "longitude") {
      newValue = value.replace(/[^0-9.\-]/g, "");
    }

    if (name === "dob") {
      const age = getAge(newValue);
      if (age !== null && age < 21) {
        setErrorMsg("You must be at least 21 years old to complete the KYC verification.");
      } else {
        setErrorMsg("");
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: files && files.length > 0 ? files[0] : newValue,
    }));
  };

  // handler for radio/select based additional-info fields
  const handleAdditionalField = (name, val) => {
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrorMsg("Geolocation is not supported by your browser.");
      return;
    }

    setGpsLoading(true);
    setErrorMsg("Fetching GPS location...");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setForm((prev) => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6),
        }));
        setErrorMsg("✅ GPS location captured successfully.");
        setGpsLoading(false);
      },
      (error) => {
        console.error("GPS error:", error);
        setErrorMsg("Failed to fetch GPS location. Please enter manually.");
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Proceed from Step1 to Step2 (DigiLocker -> Personal Info)
  const goToStep2 = () => {
    if (!digilockerVerified) {
      setErrorMsg("* Please complete DigiLocker verification before proceeding.");
      return;
    }
    setErrorMsg("");
    setShowDisclaimer(true);
  };

  // After disclaimer accepted, Step2 shown. This function validates Step2 fields and moves to Step3 (additional info)
  const goToStep3 = () => {
    if (!form.name || !form.father || !form.dob || !form.mobile || !form.address || !form.state || !form.pincode) {
      setErrorMsg("* Please fill the required details.");
      return;
    }

    const age = getAge(form.dob);
    if (age === null || age < 21) {
      setErrorMsg("Please select age 21 or above");
      return;
    }

    if (!/^[0-9]{10}$/.test(form.mobile)) {
      setErrorMsg("* Mobile must be 10 digits.");
      return;
    }

    setErrorMsg("");
    setStep(3); // new additional info page
  };

  // Validate additional info and then go to photo (start camera)
  const goToStep4 = () => {
    // Basic validation: ensure key yes/no fields answered + carryArea
    const required = [
      "convicted",
      "appliedBefore",
      "licenseSuspended",
      "familyHasLicence",
      "safePlaceToKeep",
      "trainingUnderRule10",
      "carryArea"
    ];
    for (const f of required) {
      if (!form[f]) {
        setErrorMsg("* Please answer all the additional information questions.");
        return;
      }
    }

    setErrorMsg("");
    setStep(4);
    startCamera();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consent) {
      setErrorMsg("* Please agree to the consent.");
      return;
    }
    if (!form.candidatePhoto) {
      setErrorMsg("* Please capture a photo before submitting.");
      return;
    }

    setErrorMsg("");
    try {
      setSubmitting(true);

      // 1) Upload each file directly to S3 (if present)
      let fileKey = null;
      let candidatePhotoKey = null;
      let aadhaarPhotoKey = null;
      let trainingCertificateKey = null;
      let extraDocumentKey = null;

      if (form.file) {
        fileKey = await uploadToS3ViaPresign(form.file);
      }

      if (form.candidatePhoto) {
        candidatePhotoKey = await uploadToS3ViaPresign(form.candidatePhoto);
      }

      if (form.aadhaarPhoto) {
        aadhaarPhotoKey = await uploadToS3ViaPresign(form.aadhaarPhoto);
      }

      if (form.trainingCertificate) {
        trainingCertificateKey = await uploadToS3ViaPresign(form.trainingCertificate);
      }

      if (form.extraDocument) {
        extraDocumentKey = await uploadToS3ViaPresign(form.extraDocument);
      }

      const payload = {
        aadhaar: form.aadhaar || '',
        name: form.name || '',
        father: form.father || '',
        dob: form.dob || '',
        mobile: form.mobile || '',
        address: form.address || '',
        state: form.state || '',
        pincode: form.pincode || '',
        latitude: form.latitude || '',
        longitude: form.longitude || '',
        digilockerVerificationId: digilockerVerificationId || '',
        aadhaarPhotoUrl: aadhaarPhotoUrl || '',

        // NEW additional fields grouped
        otherParticulars: {
          convicted: form.convicted || '',
          appliedBefore: form.appliedBefore || '',
          licenseSuspended: form.licenseSuspended || '',
          familyHasLicence: form.familyHasLicence || '',
          safePlaceToKeep: form.safePlaceToKeep || '',
          trainingUnderRule10: form.trainingUnderRule10 || '',
          purposeDetails: form.purposeDetails || '',
          carryArea: form.carryArea || '',
        },

        // S3 keys (if uploaded via presign)
        fileKey,
        candidatePhotoKey,
        aadhaarPhotoKey,
        trainingCertificateKey,
        extraDocumentKey,
      };

      const res = await axios.post(`${API_BASE}/api/kyc`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      console.log("KYC submission response:", res.data);

      stopCamera();
      navigate("/confirmation");
    } catch (err) {
      console.error("KYC submit error:", err?.response || err);
      setErrorMsg("Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    try { stopCamera(); } catch (e) {}
    try {
      localStorage.removeItem("videoKyc_logged_in");
      localStorage.removeItem("videoKyc_token");
      localStorage.removeItem("user_logged_in");
      sessionStorage.removeItem("isAuthenticated");
    } catch (e) {
      console.warn("Could not clear localStorage during logout", e);
    }
    navigate("/video-login", { replace: true });
  };

  const StepIndicator = ({ number, label }) => {
    const done = step > number;
    const active = step === number;
    return (
      <div className="flex flex-col items-center flex-1 text-center">
        <div
          className={`w-9 h-9 flex items-center justify-center rounded-full font-bold ${
            done ? "bg-green-600 text-white" : active ? "bg-[#40BEB4] text-white" : "bg-gray-200 text-gray-500"
          }`}
        >
          {done ? "✓" : number}
        </div>
        <span className={`mt-1 text-xs ${active || done ? "text-[#40BEB4] font-semibold" : "text-gray-400"}`}>
          {label}
        </span>
      </div>
    );
  };

  /* -------------------------
     Render Steps
     ------------------------- */

  const renderStep1 = () => (
    <motion.div
      key="step1"
      variants={stepVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="space-y-4"
    >
      <div>
        <label className={labelCls} htmlFor="aadhaar">Aadhaar (masked)</label>
        <input
          type="text"
          id="aadhaar"
          name="aadhaar"
          placeholder="e.g., XXXXXXXX1234"
          className={inputCls}
          value={form.aadhaar}
          onChange={(e) => {
            const value = e.target.value.replace(/[^xX0-9]/g, "").slice(0, 14);
            handleChange({ target: { name: "aadhaar", value } });
          }}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <button
          type="button"
          onClick={startDigilocker}
          className="w-full sm:w-auto px-6 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors"
          style={{ background: "linear-gradient(135deg, #1a1a2e, #16213e)" }}
        >
          Verify via DigiLocker
        </button>

        {digilockerStatus && (
          <span
            className={`text-sm break-words leading-snug font-medium ${
              digilockerVerified
                ? "text-green-700"
                : digilockerStatus === "EXPIRED" || digilockerStatus === "CONSENT_DENIED"
                ? "text-red-600"
                : "text-gray-700"
            }`}
          >
            Status: {digilockerStatus}
          </span>
        )}

        {(digilockerStatus === "EXPIRED" || digilockerStatus === "CONSENT_DENIED") && (
          <button
            type="button"
            onClick={startDigilocker}
            className="bg-gray-800 text-white py-1 px-3 rounded text-sm"
          >
            Resend Link
          </button>
        )}
      </div>

      {digilockerVerified && (
        <div className="text-xs bg-green-50 border border-green-300 text-green-800 p-2 rounded font-medium">
          ✅ DigiLocker verified. Aadhaar details fetched and pre-filled below.
        </div>
      )}

      <div>
        <label className={labelCls} htmlFor="file">Aadhaar / ID Document (optional)</label>
        <input
          type="file"
          id="file"
          name="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="border border-gray-300 p-2 w-full rounded bg-white text-gray-700 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#40BEB4] file:text-white file:cursor-pointer"
          onChange={handleChange}
        />
      </div>

      <div className="border-t border-gray-200 pt-4 mt-4">
        <h3 className="font-semibold text-[#40BEB4] mb-2">📍 GPS Coordinates</h3>
        <div className="flex gap-4 mb-2">
          <div className="flex-1">
            <label className={labelCls} htmlFor="latitude">Latitude</label>
            <input
              type="text"
              id="latitude"
              name="latitude"
              placeholder="Enter latitude"
              className={inputCls}
              value={form.latitude}
              onChange={handleChange}
            />
          </div>

          <div className="flex-1">
            <label className={labelCls} htmlFor="longitude">Longitude</label>
            <input
              type="text"
              id="longitude"
              name="longitude"
              placeholder="Enter longitude"
              className={inputCls}
              value={form.longitude}
              onChange={handleChange}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={getCurrentLocation}
          className={`py-2 px-4 rounded text-white font-medium ${
            gpsLoading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
          }`}
          disabled={gpsLoading}
        >
          {gpsLoading ? "Fetching Location..." : "Get Current Location Automatically"}
        </button>

        <div className="mt-4">
          <div className="mb-2 text-sm text-gray-600">
            Tip: Click on the map to set Latitude/Longitude. You can also drag the marker.
          </div>

          <div className="rounded border border-gray-300 overflow-hidden" style={{ height: 320 }}>
            <MapContainer
              center={mapCenter}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              <FlyToCenter center={mapCenter} />
              <ClickToSetLatLng />

              {!isNaN(parseFloat(form.latitude)) && !isNaN(parseFloat(form.longitude)) && (
                <Marker
                  position={[parseFloat(form.latitude), parseFloat(form.longitude)]}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const pos = e.target.getLatLng();
                      setForm((prev) => ({
                        ...prev,
                        latitude: pos.lat.toFixed(6),
                        longitude: pos.lng.toFixed(6),
                      }));
                    },
                  }}
                />
              )}
            </MapContainer>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={goToStep2}
          className="px-8 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors"
          style={{ background: "linear-gradient(135deg, #40BEB4, #2a9d93)" }}
        >
          Next
        </button>
      </div>

      {errorMsg && <p className="text-red-600 text-sm font-medium">{errorMsg}</p>}
    </motion.div>
  );

  const renderStep2 = () => {
    const today = new Date().toISOString().split("T")[0];
    return (
      <motion.div key="step2" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-4">
        <div>
          <label className={labelCls} htmlFor="name">Full Name</label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Enter full name"
            className={inputCls}
            value={form.name}
            onChange={handleChange}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="father">Father Name</label>
          <input
            type="text"
            id="father"
            name="father"
            placeholder="Enter Father name"
            className={inputCls}
            value={form.father}
            onChange={handleChange}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="dob">Date of Birth</label>
          <input
            type="date"
            id="dob"
            name="dob"
            max={today}
            className={inputCls}
            value={form.dob}
            onChange={handleChange}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="mobile">Mobile Number</label>
          <input
            type="tel"
            id="mobile"
            name="mobile"
            placeholder="Enter 10-digit mobile number"
            className={inputCls}
            value={form.mobile}
            inputMode="numeric"
            onChange={handleChange}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="address">Address</label>
          <textarea
            id="address"
            name="address"
            placeholder="Enter address as in Aadhaar"
            className={`${inputCls} resize-none`}
            rows={3}
            value={form.address}
            onChange={handleChange}
          />
        </div>

        <div>
          <label className={labelCls} htmlFor="state">State</label>
          <select
            id="state"
            name="state"
            value={form.state}
            onChange={handleChange}
            required
            className={inputCls}
          >
            <option value="">-- Select State --</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
            <option value="Chandigarh">Chandigarh</option>
            <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
            <option value="Delhi">Delhi</option>
            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            <option value="Ladakh">Ladakh</option>
            <option value="Lakshadweep">Lakshadweep</option>
            <option value="Puducherry">Puducherry</option>
          </select>
        </div>

        <div>
          <label className={labelCls} htmlFor="pincode">Pincode</label>
          <input
            type="text"
            id="pincode"
            name="pincode"
            value={form.pincode}
            placeholder="Enter 6-digit pincode"
            required
            maxLength={6}
            inputMode="numeric"
            className={inputCls}
            onChange={handleChange}
          />
        </div>

        {(aadhaarPhotoPreview || aadhaarPhotoUrl) && (
          <div className="border border-gray-200 rounded p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-[#40BEB4]">Aadhaar Photo (Auto-fetched)</h4>
              <button
                type="button"
                className="text-xs underline text-blue-700"
                onClick={async () => {
                  if (!aadhaarPhotoUrl) return;
                  try {
                    let file = null;
                    if (/^data:/i.test(aadhaarPhotoUrl)) {
                      file = await dataUrlToFile(aadhaarPhotoUrl, "aadhaar-photo.jpg");
                    } else if (looksLikeBase64(aadhaarPhotoUrl)) {
                      file = await base64ToFile(aadhaarPhotoUrl, "aadhaar-photo.jpg", "image/jpeg");
                    } else if (/^https?:\/\//i.test(aadhaarPhotoUrl)) {
                      file = await fetchRemoteImageAsFile(aadhaarPhotoUrl, "aadhaar-photo.jpg");
                    }
                    if (file) {
                      setForm((prev) => ({ ...prev, aadhaarPhoto: file }));
                      setAadhaarPhotoPreview(URL.createObjectURL(file));
                    } else {
                      alert("Could not refresh the image.");
                    }
                  } catch (e) {
                    alert("Could not refresh the image from source.");
                  }
                }}
              >
                Refresh Photo
              </button>
            </div>

            <div className="flex items-center gap-4">
              <img
                src={aadhaarPhotoPreview || aadhaarPhotoUrl}
                alt="Aadhaar"
                className="w-24 h-24 object-cover rounded border border-gray-300"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="text-xs text-gray-600 break-all">
                <div className="font-semibold">Photo Link:</div>
                <div className="truncate max-w-[16rem]" title={aadhaarPhotoUrl}>
                  {aadhaarPhotoUrl || "N/A"}
                </div>
                {!form.aadhaarPhoto && aadhaarPhotoUrl && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    Preview might be blocked by CORS in some browsers. We'll still submit the link.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between gap-3 pt-2">
          <button
            onClick={() => setStep(1)}
            className="flex-1 py-2.5 rounded-lg font-semibold text-sm border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            onClick={goToStep3}
            className="flex-1 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors"
            style={{ background: "linear-gradient(135deg, #40BEB4, #2a9d93)" }}
          >
            Next
          </button>
        </div>
        {errorMsg && <p className="text-red-600 text-sm font-medium">{errorMsg}</p>}
      </motion.div>
    );
  };

  // Step 3 - Additional information (updated)
  const renderStep3Additional = () => (
    <motion.div key="step3-add" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-4">
      <h4 className="font-semibold text-lg text-gray-800">Other particulars of the applicant</h4>
      <div className="grid gap-3">
        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Has the applicant ever been involved in any criminal activity?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="convicted" checked={form.convicted==="yes"} onChange={()=>handleAdditionalField("convicted","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="convicted" checked={form.convicted==="no"} onChange={()=>handleAdditionalField("convicted","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Whether the applicant applied for a licence before?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="appliedBefore" checked={form.appliedBefore==="yes"} onChange={()=>handleAdditionalField("appliedBefore","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="appliedBefore" checked={form.appliedBefore==="no"} onChange={()=>handleAdditionalField("appliedBefore","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Whether the applicant's licence was ever suspended/cancelled/revoked?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="licenseSuspended" checked={form.licenseSuspended==="yes"} onChange={()=>handleAdditionalField("licenseSuspended","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="licenseSuspended" checked={form.licenseSuspended==="no"} onChange={()=>handleAdditionalField("licenseSuspended","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Any other member of the applicant's family in possession of arms licence?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="familyHasLicence" checked={form.familyHasLicence==="yes"} onChange={()=>handleAdditionalField("familyHasLicence","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="familyHasLicence" checked={form.familyHasLicence==="no"} onChange={()=>handleAdditionalField("familyHasLicence","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Does the applicant have a safe place to keep the arms and ammunition?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="safePlaceToKeep" checked={form.safePlaceToKeep==="yes"} onChange={()=>handleAdditionalField("safePlaceToKeep","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="safePlaceToKeep" checked={form.safePlaceToKeep==="no"} onChange={()=>handleAdditionalField("safePlaceToKeep","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Has the applicant undergone training as specified under rule 10?</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="trainingUnderRule10" checked={form.trainingUnderRule10==="yes"} onChange={()=>handleAdditionalField("trainingUnderRule10","yes")} /> Yes</label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer"><input type="radio" name="trainingUnderRule10" checked={form.trainingUnderRule10==="no"} onChange={()=>handleAdditionalField("trainingUnderRule10","no")} /> No</label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Area within which applicant wishes to carry arms</label>
          <div className="flex gap-4 items-center flex-wrap">
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer">
              <input type="radio" name="carryArea" checked={form.carryArea === "throughout"} onChange={() => handleAdditionalField("carryArea", "throughout")} />
              Throughout India
            </label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer">
              <input type="radio" name="carryArea" checked={form.carryArea === "state"} onChange={() => handleAdditionalField("carryArea", "state")} />
              State
            </label>
            <label className="inline-flex items-center gap-2 text-gray-700 cursor-pointer">
              <input type="radio" name="carryArea" checked={form.carryArea === "district"} onChange={() => handleAdditionalField("carryArea", "district")} />
              District
            </label>
          </div>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Purpose(s) for which licence is required (mention purpose)</label>
          <textarea
            value={form.purposeDetails}
            onChange={(e)=>handleAdditionalField("purposeDetails", e.target.value)}
            className={`${inputCls} resize-none`}
            rows={3}
            placeholder="e.g., protection against wild animals, sport, display, etc."
          />
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Training certificate (jpeg / jpg / png / pdf) — max 5 MB</label>
          <input
            type="file"
            name="trainingCertificate"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleChange}
            className="w-full text-gray-700 border border-gray-300 rounded p-2 bg-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#40BEB4] file:text-white file:cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">Upload the training certificate (if any). If large file, use PDF.</p>
        </QuestionCard>

        <QuestionCard>
          <label className="block mb-1 font-semibold text-gray-700">Additional document (optional)</label>
          <input
            type="file"
            name="extraDocument"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleChange}
            className="w-full text-gray-700 border border-gray-300 rounded p-2 bg-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#40BEB4] file:text-white file:cursor-pointer"
          />
          <p className="text-xs text-gray-500 mt-1">Any other supporting document you want to attach.</p>
        </QuestionCard>
      </div>

      <div className="flex justify-between gap-3 pt-2">
        <button
          onClick={() => setStep(2)}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={goToStep4}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors"
          style={{ background: "linear-gradient(135deg, #40BEB4, #2a9d93)" }}
        >
          Next
        </button>
      </div>

      {errorMsg && <p className="text-red-600 text-sm font-medium">{errorMsg}</p>}
    </motion.div>
  );

  // Step 4: Photo & Consent (styled)
  const renderStep4 = () => (
    <motion.form
      key="step4"
      variants={stepVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      onSubmit={handleSubmit}
      className="space-y-4"
    >

      {!photoPreview && (
        <div className="flex flex-col items-center space-y-3">
          <video
            ref={videoRef}
            id="cameraVideo"
            className="border-2 border-gray-300 rounded w-56 h-44 bg-black"
            autoPlay
            playsInline
          ></video>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={startCamera}
              className="bg-blue-600 text-white px-3 py-1 rounded font-medium"
            >
              Start Camera
            </button>
            <button
              type="button"
              onClick={toggleCamera}
              className="bg-[#40BEB4] text-white px-3 py-1 rounded font-medium"
            >
              Switch Camera
            </button>
            <button
              type="button"
              onClick={() => {
                if (!videoRef.current || videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
                  alert("Camera not ready yet. Please wait a moment.");
                  return;
                }

                const canvas = document.createElement("canvas");
                canvas.width = videoRef.current.videoWidth;
                canvas.height = videoRef.current.videoHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(videoRef.current, 0, 0);

                canvas.toBlob((blob) => {
                  if (!blob) {
                    alert("Failed to capture photo. Try again.");
                    return;
                  }
                  const file = new File([blob], "candidate-photo.jpg", { type: "image/jpeg" });
                  setForm((prev) => ({ ...prev, candidatePhoto: file }));
                  setPhotoPreview(URL.createObjectURL(file));
                }, "image/jpeg");

                stopCamera();
              }}
              className="bg-green-600 text-white px-3 py-1 rounded font-medium"
            >
              Capture
            </button>
          </div>
        </div>
      )}

      {photoPreview && (
        <div className="mt-2 flex flex-col items-center gap-2">
          <img
            src={photoPreview}
            alt="Candidate preview"
            className="h-28 w-28 object-cover rounded border-2 border-gray-300"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setForm((prev) => ({ ...prev, candidatePhoto: null }));
                setPhotoPreview(null);
                startCamera();
              }}
              className="text-xs text-red-600 underline"
            >
              Retake Photo
            </button>
            <button
              type="button"
              onClick={() => {
                const a = document.createElement("a");
                a.href = photoPreview;
                a.download = "candidate-photo.jpg";
                a.click();
              }}
              className="text-xs text-gray-700 underline"
            >
              Download Preview
            </button>
          </div>
        </div>
      )}

      <div>
        <label className={labelCls} htmlFor="serialNumber">
          Serial Number(s)
        </label>
        <input
          id="serialNumber"
          name="serialNumber"
          type="text"
          value=""
          disabled
          readOnly
          placeholder="To be assigned later in Dashboard"
          className="border border-gray-200 p-2 w-full rounded bg-gray-100 cursor-not-allowed placeholder-gray-400 text-gray-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          This will be assigned from the Dashboard (supports multiple values).
        </p>
      </div>

      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id="consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 flex-shrink-0"
        />
        <label htmlFor="consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
          I hereby declare that the above particulars given in the application are true, complete and correct to the best of my knowledge and belief. I understand that in the event of any information being found false or incorrect at any stage, I am liable to be proceeded against and action taken under the relevant provisions of the Arms Act, 1959, the Arms Rules, 2016, and other central enactments or the law for the time being in force. I consent to share my details with Forest Department and Advitiya Labs.
        </label>
      </div>

      {errorMsg && <p className="text-red-600 text-sm font-medium">{errorMsg}</p>}

      <WarningBox>
        Suppression of any factual information or furnishing of any false or wrong information in the application form in violation of arms rules will render the applicant liable for punishment under Section 30 of the Arms Act, 1959.
      </WarningBox>

      <div className="flex justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={() => {
            stopCamera();
            setStep(3);
          }}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!consent || submitting}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "linear-gradient(135deg, #40BEB4, #2a9d93)" }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </motion.form>
  );

  return (
    /* FIX 3: single scroll on the page-level wrapper only */
    <div
      style={{
        minHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        background: "linear-gradient(160deg, #071520 0%, #0a1e2e 35%, #071018 100%)",
        /* FIX 1: removed kfd.png watermark — plain background */
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        /* FIX 2: padding so card sits nicely; no orphan dark area */
        padding: "40px 16px",
        boxSizing: "border-box",
      }}
    >
      {/* ── LIGHT CARD ── */}
      <div
        className="relative z-10 w-full max-w-2xl flex flex-col items-center py-10 px-6 rounded-xl"
        style={{
          background: "#ffffff",
          border: "1px solid rgba(64,190,180,0.35)",
          boxShadow: `
            0 0 0 1px rgba(255,255,255,0.8) inset,
            0 20px 60px rgba(0,0,0,0.5),
            0 0 40px rgba(64,190,180,0.12)
          `,
          /* FIX 2: card grows to fit content; no overflow:hidden cutting it short */
          overflow: "visible",
        }}
      >
        {/* subtle teal top accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
          style={{ background: "linear-gradient(90deg, #40BEB4, #2a9d93)" }}
        />

        <div className="absolute top-4 right-4">
          <button
            onClick={handleLogout}
            className="text-xs bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded-md hover:bg-red-100 font-medium"
            title="Logout and return to Video Login"
          >
            Logout
          </button>
        </div>

        <h2
          className="text-3xl font-bold text-center mb-8 pb-3 w-full"
          style={{ color: "#40BEB4", borderBottom: "2px solid rgba(64,190,180,0.25)" }}
        >
          KYC Process
        </h2>

        <div className="flex justify-between mb-10 w-full px-2">
          <StepIndicator number={1} label="DigiLocker" />
          <StepIndicator number={2} label="Personal Info" />
          <StepIndicator number={3} label="Additional Info" />
          <StepIndicator number={4} label="Photo & Consent" />
        </div>

        {/* Step connector line */}
        <div className="w-full mb-4 px-10">
          <div className="h-px bg-gray-200 w-full" />
        </div>

        <div className="w-full">
          <AnimatePresence mode="wait">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3Additional()}
            {step === 4 && renderStep4()}
          </AnimatePresence>
        </div>

        {showDisclaimer && (
          <DisclaimerModal
            onAccept={() => {
              setShowDisclaimer(false);
              setStep(2);
            }}
            onCancel={() => setShowDisclaimer(false)}
          />
        )}
        <style>{`
          .leaflet-container { z-index: 0 !important; }
          .leaflet-pane,
          .leaflet-top,
          .leaflet-bottom,
          .leaflet-marker-pane,
          .leaflet-popup-pane,
          .leaflet-overlay-pane,
          .leaflet-shadow-pane,
          .leaflet-tile-pane,
          .leaflet-control-container { z-index: 0 !important; }
        `}</style>
      </div>
    </div>
  );
};

const DisclaimerModal = ({ onAccept, onCancel }) => (
  <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4">
    <div
      className="w-full max-w-sm p-8 text-center rounded-2xl bg-white"
      style={{
        border: "1px solid rgba(64,190,180,0.35)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3), 0 0 40px rgba(64,190,180,0.12)",
      }}
    >
      {/* Icon */}
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center"
          style={{ background: "rgba(64,190,180,0.1)", border: "2px solid rgba(64,190,180,0.4)" }}>
          <svg className="w-7 h-7" style={{ color: "#40BEB4" }} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
          </svg>
        </div>
      </div>
      <h3 className="text-xl font-bold text-[#40BEB4] mb-2">Important</h3>
      <p className="text-sm text-gray-600 mb-7 leading-relaxed">
        Please fill the same details as on your Aadhaar card in the next step.
      </p>
      <div className="flex justify-center gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={onAccept}
          className="flex-1 py-2.5 rounded-lg font-semibold text-sm text-white transition-colors"
          style={{ background: "linear-gradient(135deg, #40BEB4, #2a9d93)" }}
        >
          Continue
        </button>
      </div>
    </div>
  </div>
);

export default KycForm;
